/**
 * Editorial Full Card — Swiper carousel (AUR-199), CTAs (data-lvz-cta-button), aria-live updates.
 */
import Swiper from 'swiper';
import { A11y, Keyboard, Navigation, Pagination } from 'swiper/modules';
import { decorateCarouselPagination, syncNonLoopCarouselNavState, updateCarouselLiveRegion } from './utils/lvzCarouselA11y';
import { syncCtaAriaLabel } from './utils/lvzCtaA11y';

(function initLvzFullCard() {
	/**
	 * @param {HTMLButtonElement} button
	 */
	function navigateCta(button) {
		const href = button.getAttribute('data-lvz-cta-href');
		const target = button.getAttribute('data-lvz-cta-target') || '_self';
		if (!href) {
			return;
		}
		if (target === '_blank') {
			window.open(href, '_blank', 'noopener,noreferrer');
		} else {
			window.location.assign(href);
		}
	}

	/**
	 * @param {Element} root
	 */
	function bindCtas(root) {
		root.querySelectorAll('[data-lvz-cta-button]').forEach(function (el) {
			syncCtaAriaLabel(el);
			el.addEventListener('click', function onCtaClick(e) {
				e.preventDefault();
				navigateCta(el);
			});
		});
	}

	/**
	 * Page Designer wraps each region child in an extra element (e.g. .experience-component).
	 * Swiper only counts direct children of .swiper-wrapper as slides; move .swiper-slide
	 * from the inner root (e.g. article) onto that wrapper before init.
	 *
	 * @param {Element} swiperRoot - .swiper container
	 */
	function normalizePdSwiperSlides(swiperRoot) {
		const wrapper = swiperRoot.querySelector('.swiper-wrapper');
		if (!wrapper) {
			return;
		}
		Array.prototype.forEach.call(wrapper.children, function (child) {
			if (child.classList.contains('swiper-slide')) {
				return;
			}
			const nested = child.querySelector(':scope > .swiper-slide');
			if (nested) {
				child.classList.add('swiper-slide');
				nested.classList.remove('swiper-slide');
			}
		});
	}

	/**
	 * @param {Element} section
	 */
	function initSwiperForSection(section) {
		if (section.getAttribute('data-lvz-full-card-swiper-init') === 'true') {
			return;
		}
		const swiperEl = section.querySelector('[data-lvz-full-card-swiper]');
		if (!swiperEl) {
			return;
		}

		const cardCount = Number.parseInt(section.getAttribute('data-slide-count'), 10);
		if (Number.isNaN(cardCount) || cardCount < 1) {
			return;
		}

		const desktopCarousel = section.getAttribute('data-desktop-carousel') === 'true';
		const mobileCarousel = section.getAttribute('data-mobile-carousel') === 'true';
		const live = section.querySelector('.lvz-full-card__live');
		const positionPattern = 'Slide {0} of {1}';

		const paginationEl = swiperEl.querySelector('.lvz-full-card__pagination');
		const prevEl = swiperEl.querySelector('.swiper-button-prev');
		const nextEl = swiperEl.querySelector('.swiper-button-next');

		/** @type {Record<string, unknown>} */
		const config = {
			modules: [Navigation, Pagination, A11y, Keyboard],
			slidesPerView: 'auto',
			spaceBetween: 8,
			slidesOffsetBefore: 24,
			slidesOffsetAfter: 24,
			watchOverflow: cardCount > 3,
			observer: true,
			observeParents: true,
			grabCursor: true,
			a11y: {
				enabled: true,
			},
			on: {
				slideChange: function onSlideChange(sw) {
					updateCarouselLiveRegion(live, positionPattern, sw.activeIndex, cardCount);
					syncNonLoopCarouselNavState(sw, prevEl, nextEl);
				},
				init: function onInit(sw) {
					updateCarouselLiveRegion(live, positionPattern, sw.activeIndex, cardCount);
					decorateCarouselPagination(section, '.lvz-full-card__pagination', positionPattern, cardCount);
					syncNonLoopCarouselNavState(sw, prevEl, nextEl);
				},
			},
		};

		if (paginationEl) {
			config.pagination = {
				el: paginationEl,
				clickable: true,
				enabled: mobileCarousel,
			};
		}

		if (prevEl && nextEl) {
			config.navigation = {
				prevEl: prevEl,
				nextEl: nextEl,
				enabled: false,
				addIcons: false,
			};
		}

		config.breakpoints = {
			1024: {
				slidesPerView: 'auto',
				spaceBetween: 16,
				slidesOffsetBefore: 80,
				slidesOffsetAfter: 80,
				centerInsufficientSlides: !desktopCarousel,
				allowTouchMove: desktopCarousel,
			},
		};

		if (paginationEl && config.pagination) {
			config.breakpoints[1024].pagination = Object.assign({}, config.pagination, {
				enabled: desktopCarousel,
			});
		}

		if (prevEl && nextEl && config.navigation) {
			config.breakpoints[1024].navigation = Object.assign({}, config.navigation, {
				enabled: desktopCarousel,
			});
		}

		normalizePdSwiperSlides(swiperEl);

		const swiper = new Swiper(swiperEl, config);
		section.setAttribute('data-lvz-full-card-swiper-init', 'true');

		requestAnimationFrame(function () {
			swiper.update();
			syncNonLoopCarouselNavState(swiper, prevEl, nextEl);
		});
		window.addEventListener(
			'resize',
			function onFullCardResize() {
				swiper.update();
				syncNonLoopCarouselNavState(swiper, prevEl, nextEl);
			},
			{ passive: true }
		);
	}

	/**
	 * @param {Element} section
	 */
	function initOne(section) {
		bindCtas(section);
		initSwiperForSection(section);
	}

	function boot() {
		document.querySelectorAll('[data-lvz-full-card]').forEach(function (el) {
			initOne(el);
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', boot);
	} else {
		boot();
	}
})();
